// Demo Maurice — honest presentation layer (frontend only).
//
// Applies ONLY to an immutable Demo organisation whose destination is Mauritius
// (see isMauritiusDemoOrg). The selector exposes seven markets backed by live
// Lodge data. Air fallback is limited to the seven approved ADI routes below.
// No figure is invented here: every
// metric that is not served by the API stays `null` and is rendered as
// "Donnée non disponible".
// Real Lodge Market Data (Sabre ADI air) is always fetched from the API and never
// copied into this file. Other organisations keep their existing behaviour.
import { isMauritiusDestination } from "@/lib/mauritiusDemoJourney";
import { MARKET_INTELLIGENCE_DEMO } from "@/lib/marketIntelligenceDemo";

export const DATA_UNAVAILABLE = "Donnée non disponible";

export const DEMO_MAURITIUS_ROUTES = [
  { key: "FR-MRU", iso: "FR", name: "France", label: "France → Maurice" },
  { key: "GB-MU", iso: "GB", name: "Royaume-Uni", label: "Royaume-Uni → Maurice" },
  { key: "DE-MU", iso: "DE", name: "Allemagne", label: "Allemagne → Maurice" },
  { key: "US-MU", iso: "US", name: "États-Unis", label: "États-Unis → Maurice" },
  { key: "IN-MU", iso: "IN", name: "Inde", label: "Inde → Maurice" },
  { key: "AE-MU", iso: "AE", name: "Émirats arabes unis", label: "Émirats arabes unis → Maurice" },
  { key: "JP-MU", iso: "JP", name: "Japon", label: "Japon → Maurice" },
];

export const DEMO_MAURITIUS_MARKET_KEYS = DEMO_MAURITIUS_ROUTES.map((route) => route.key);

export const DEMO_MAURITIUS_CATALOG_KEYS = [...DEMO_MAURITIUS_MARKET_KEYS];

export const isMauritiusDemoOrg = (org) =>
  !!org && org.is_demo === true && org.demo_mode === "immutable" && isMauritiusDestination(org);

export const isDemoMauritiusMarketKey = (key) =>
  DEMO_MAURITIUS_CATALOG_KEYS.includes(String(key || "").toUpperCase());

// Resolve a route from a canonical key (FR-MRU), an ISO origin (FR) or a route
// with an exact approved route. Anything else is outside the Demo perimeter.
export const demoMauritiusRouteFor = (keyOrIso) => {
  const value = String(keyOrIso || "").trim().toUpperCase();
  if (!value) return null;
  return DEMO_MAURITIUS_ROUTES.find((route) => route.key === value)
    || DEMO_MAURITIUS_ROUTES.find((route) => route.iso === value)
    || null;
};

// Defence in depth: expose only the seven supported Demo market views.
export const filterDemoMauritiusCatalog = (markets = []) =>
  (markets || []).filter((market) => isDemoMauritiusMarketKey(market?.value ?? market?.key));

// One row per approved route. Tourism metrics are unavailable; air passengers are
// filled ONLY from real API totals passed in by the caller (key -> raw passengers).
export const buildDemoMauritiusMarketRows = (airPassengersByKey = {}) =>
  DEMO_MAURITIUS_ROUTES.map((route) => {
    const air = airPassengersByKey?.[route.key];
    const hasAir = typeof air === "number" && Number.isFinite(air);
    return {
      key: route.iso,
      iso: route.iso,
      name: route.name,
      air_market_key: route.key,
      volume: null,
      evolution: null,
      part_destination: null,
      air_passengers: hasAir ? air : null,
      spend: null,
      length_of_stay: null,
      lodge_score: null,
      volume_metric: null,
      source_layer: hasAir ? "LODGE" : null,
      sources: hasAir ? { air: "LODGE" } : {},
    };
  });

export const buildDemoMauritiusMarketsPayload = (airPassengersByKey = {}) => ({
  has_data: true,
  is_demo: true,
  simulation: false,
  data_unavailable: true,
  source_type: "lodge",
  source: "Lodge Market Data (aérien) — données touristiques non disponibles",
  role: "DEMO",
  filters: {
    destinations: [{ value: "MU", label: "Maurice" }],
    periods: [],
    source_markets: DEMO_MAURITIUS_ROUTES.map(({ iso, name }) => ({ value: iso, label: name })),
  },
  markets: buildDemoMauritiusMarketRows(airPassengersByKey),
});

const pendingKpi = (key, label) => ({ key, label, value: null, unit: null, sub: DATA_UNAVAILABLE, delta: null, pending: true });

// Fallback used by useMarketIntelligence for the Demo Maurice before/without live
// ADI data: same shape as MARKET_INTELLIGENCE_DEMO, without any illustrative value
// (no generic airlines, origins, macro context, signals or news).
export const DEMO_MAURITIUS_MI = {
  ...MARKET_INTELLIGENCE_DEMO,
  unavailable: true,
  market: { value: "FR-MRU", label: "France → Maurice", origin: "France", destination: "Maurice" },
  period: { label: "Période non disponible", months: MARKET_INTELLIGENCE_DEMO.period.months },
  kpis: {
    airPassengers: pendingKpi("airPassengers", "Passagers aériens"),
    airRevenue: pendingKpi("airRevenue", "Revenus aériens"),
    averageFare: pendingKpi("averageFare", "Tarif moyen"),
    internationalVisitors: pendingKpi("internationalVisitors", "Visiteurs internationaux"),
  },
  topAirlinesByPassengers: [],
  topAirlinesByRevenue: [],
  monthly: [],
  visitorSeasonality: [],
  visitorOrigins: [],
  economicContext: [],
  intelligenceSignals: [],
  marketNews: [],
  dataSources: [
    { name: "Lodge Data", desc: "Aérien Sabre ADI — sept marchés émetteurs → Maurice", status: "À venir" },
    { name: "Offices du Tourisme", desc: "Arrivées visiteurs par marché", status: "Non disponible" },
  ],
};

// Fallback market sheet for the seven approved routes only. Air data is NOT set
// here: the page uses the real shared ADI response when it exists.
export const demoMauritiusMarketDetail = (marketKey) => {
  // Exact route key or ISO origin only: out-of-scope routes (FR-WORLD, MU-FR, ...) never resolve.
  const route = demoMauritiusRouteFor(marketKey);
  if (!route) return null;
  return {
    is_demo: true,
    simulation: false,
    data_unavailable: true,
    market: {
      key: route.iso, iso: route.iso, name: route.name, air_market_key: route.key,
      volume: null, evolution: null, part_destination: null, spend: null,
      length_of_stay: null, lodge_score: null, seasonality: null, sources: {},
    },
    air: null,
    observations: [],
    recommendation: null,
    signals: [],
  };
};

// Deterministic, figure-free demo analysis (no AI call, no credit).
export const demoMauritiusAiResult = (context = {}) => {
  const route = demoMauritiusRouteFor(context.marketKey);
  const label = route ? route.label : (context.market || "ce marché");
  return {
    summary: route
      ? `Analyse de démonstration ${label} : seules les données aériennes agrégées Lodge Market Data sont disponibles ; aucune donnée touristique n’est utilisée.`
      : `${label} n’appartient pas au périmètre de la démonstration Maurice.`,
    keySignals: [
      route ? `Données aériennes Sabre ADI disponibles pour ${label} (voir le Dashboard).` : "Aucune donnée aérienne de démonstration pour ce marché.",
      `Arrivées, évolution et part de marché touristiques : ${DATA_UNAVAILABLE.toLowerCase()}.`,
    ],
    interpretation: ["Sans série touristique par marché, aucune tendance ni aucun potentiel n’est extrapolé."],
    recommendedActions: [
      "Comparer passagers, revenus et compagnies de la liaison dans le Dashboard.",
      "Importer les arrivées par marché pour compléter l’analyse.",
    ],
    caveats: ["Analyse déterministe de démonstration : aucune IA n’est appelée et aucun crédit n’est consommé."],
    provenance: ["Démonstration Lodge & Co — Maurice"],
    confidence: "Low",
    isDemo: true,
  };
};

// Figure-free opportunity examples, one per approved route (always labelled Demo).
export const buildDemoMauritiusOpportunities = () => DEMO_MAURITIUS_ROUTES.map((route, i) => ({
  id: `demo-mu-${i}`,
  type: "demo",
  signal: route.label,
  opportunity: `${route.label} : examiner la desserte aérienne avant toute priorisation`,
  evidence: "Exemple de démonstration · données touristiques non disponibles",
  recommendedAction: "Explorer les marchés",
  source: "Demo",
  confidence: null,
  market: route.name,
  marketKey: route.key,
  objective: null,
  category: "Marché",
  potential: null,
}));

// Guided Ask Lodge answers for the Demo Maurice (same shape as DEMO_ASK_QUESTIONS).
export const DEMO_MAURITIUS_ASK_QUESTIONS = [
  {
    id: "scope",
    q: { fr: "Quels marchés couvre la démonstration ?", en: "Which markets does the demo cover?" },
    data: {
      fr: "Sept marchés émetteurs vers Maurice : France, Royaume-Uni, Allemagne, États-Unis, Inde, Émirats arabes unis et Japon. Les données aériennes agrégées Sabre ADI sont chargées pour ces sept liaisons.",
      en: "Seven source markets to Mauritius: France, the United Kingdom, Germany, the United States, India, the United Arab Emirates and Japan. Aggregated Sabre ADI air data is loaded for these seven routes.",
    },
    analysis: {
      fr: "Les arrivées, l’évolution et la dépense touristiques par marché ne sont pas disponibles dans cette démonstration.",
      en: "Tourism arrivals, growth and spend by market are not available in this demo.",
    },
    recommendation: {
      fr: "Consulter le Dashboard (sélecteur de marché) pour les indicateurs aériens observés de chaque liaison.",
      en: "Use the Dashboard market selector to see the observed air indicators of each route.",
    },
  },
  {
    id: "air",
    q: { fr: "Quel rôle joue l’aérien sur ces sept marchés ?", en: "What role does air play for these seven markets?" },
    data: {
      fr: "Passagers, revenus, tarif moyen et compagnies sont affichés par liaison à partir des données ADI chargées ; aucun chiffre n’est réécrit dans cette réponse.",
      en: "Passengers, revenue, average fare and airlines are shown per route from the loaded ADI data; no figure is restated in this answer.",
    },
    analysis: {
      fr: "Ces indicateurs décrivent l’accessibilité aérienne ; ils ne mesurent pas à eux seuls les arrivées touristiques.",
      en: "These indicators describe air accessibility; on their own they do not measure tourism arrivals.",
    },
    recommendation: {
      fr: "Comparer les liaisons et la concentration des compagnies avant de prioriser un marché.",
      en: "Compare routes and airline concentration before prioritising a market.",
    },
  },
  {
    id: "growing",
    q: { fr: "Quels marchés progressent le plus ?", en: "Which markets are growing the most?" },
    data: {
      fr: "Donnée non disponible : aucune série d’évolution touristique n’est chargée pour la France, l’Inde, le Japon ou les Émirats vers Maurice.",
      en: "Data not available: no tourism growth series is loaded for France, India, Japan or the UAE to Mauritius.",
    },
    analysis: {
      fr: "Aucune tendance n’est extrapolée ni inventée en l’absence de données observées.",
      en: "No trend is extrapolated or invented without observed data.",
    },
    recommendation: {
      fr: "Importer les arrivées mensuelles par marché pour activer cette analyse.",
      en: "Import monthly arrivals by market to enable this analysis.",
    },
  },
  {
    id: "period",
    q: { fr: "Quelle période est la plus favorable ?", en: "Which period is most favourable?" },
    data: {
      fr: "Donnée non disponible : la saisonnalité par marché n’est pas chargée dans cette démonstration.",
      en: "Data not available: seasonality by market is not loaded in this demo.",
    },
    analysis: {
      fr: "Les données aériennes disponibles sont agrégées sur la période affichée et ne permettent pas d’isoler les mois.",
      en: "The available air data is aggregated over the displayed period and cannot isolate months.",
    },
    recommendation: {
      fr: "Charger des séries mensuelles par marché avant de planifier une campagne saisonnière.",
      en: "Load monthly series by market before planning a seasonal campaign.",
    },
  },
];
